import { isNotDefined } from '@sgme/fp';

import type {
  CashIdle,
  CashRfsNotReady,
  CashRfsPending,
  CashRfsPrice,
  CashRfsRequired,
} from '@/store/cash/selectors/prices.types.ts';
import type { RfsPrice, RfsPriceStreamed } from '@/store/cash/state.ts';
import type { CurrencyIndex } from '@/utils/currencies/types.ts';
import { type BidAskPoint, type BidAskRate, convertPointsToRate, convertRateToPoints } from '@/utils/number-point.ts';
import { roundToFixed } from '@/utils/number.ts';

export const getRequestCashRfsMutationCacheKey = (tileId: string) => `cash-rfs-request-${tileId}`;

export type ForwardCashPrices = CashRfsPending | CashRfsPrice | CashRfsNotReady | CashRfsRequired | CashIdle;

export type GetRfsPricesProps = {
  currency1: string;
  currency2: string;
  rfs: RfsPriceStreamed;
  currencyPairPrecision: number;
  notional: number;
  notionalCurrencyIndex: CurrencyIndex;
  isInternalSale: boolean;
  keepEsp?: boolean;
  canTrade?: boolean;
  isStreamingConnected?: boolean;
};

export const getRfsPrices = ({
  currency1,
  currency2,
  rfs,
  currencyPairPrecision,
  notional,
  notionalCurrencyIndex,
  isInternalSale,
  keepEsp = false,
  canTrade = true,
  isStreamingConnected = true,
}: GetRfsPricesProps): CashRfsPrice => {
  const commercialMarginAmountCurrencyIndex = rfs.commercialMarginAmountCurrencyIndex;

  const commercialMarginAmountCurrency = commercialMarginAmountCurrencyIndex === 1 ? currency1 : currency2;

  const commercialMarginSpotPoints = rfs.commercialMarginSpotPoints ?? {
    bid: 0,
    ask: 0,
  };

  const spotRates = isInternalSale
    ? {
        bid: convertPointsToRate(
          convertRateToPoints(rfs.quote.spotWithoutMargin.bid, currencyPairPrecision) -
            10 * commercialMarginSpotPoints.bid,
          currencyPairPrecision + 1,
          currencyPairPrecision,
        ),
        ask: convertPointsToRate(
          convertRateToPoints(rfs.quote.spotWithoutMargin.ask, currencyPairPrecision) +
            10 * commercialMarginSpotPoints.ask,
          currencyPairPrecision + 1,
          currencyPairPrecision,
        ),
      }
    : // the spot rate displayed for a RFS = spot without margin
      {
        bid: rfs.quote.spotWithMargin.bid,
        ask: rfs.quote.spotWithMargin.ask,
      };

  const commercialMarginSwapPoints = {
    bid: rfs.commercialMarginSwapPoints?.bid ?? 0,
    ask: rfs.commercialMarginSwapPoints?.ask ?? 0,
  };

  // the swap points displayed for a RFS = forward points without margin + swap margin (initiated with forwardPointsWithMargin, and edited by the user)
  const swapPoints = isInternalSale
    ? {
        bid: roundToFixed(rfs.quote.forwardPointsWithoutMargin.bid - commercialMarginSwapPoints.bid, 2),
        ask: roundToFixed(rfs.quote.forwardPointsWithoutMargin.ask + commercialMarginSwapPoints.ask, 2),
      }
    : {
        bid: rfs.quote.forwardPointsWithMargin.bid,
        ask: rfs.quote.forwardPointsWithMargin.ask,
      };

  // the main rate displayed for a RFS
  const rates = isInternalSale
    ? {
        bid: convertPointsToRate(
          convertRateToPoints(rfs.quote.spotWithoutMargin.bid, currencyPairPrecision) +
            10 * swapPoints.bid -
            10 * commercialMarginSpotPoints.bid,
          currencyPairPrecision + 1,
          currencyPairPrecision,
        ),
        ask: convertPointsToRate(
          convertRateToPoints(rfs.quote.spotWithoutMargin.ask, currencyPairPrecision) +
            10 * swapPoints.ask +
            10 * commercialMarginSpotPoints.ask,
          currencyPairPrecision + 1,
          currencyPairPrecision,
        ),
      }
    : {
        bid: convertPointsToRate(
          convertRateToPoints(rfs.quote.spotWithMargin.bid, currencyPairPrecision) +
            10 * rfs.quote.forwardPointsWithMargin.bid,
          currencyPairPrecision + 1,
          currencyPairPrecision,
        ),
        ask: convertPointsToRate(
          convertRateToPoints(rfs.quote.spotWithMargin.ask, currencyPairPrecision) +
            10 * rfs.quote.forwardPointsWithMargin.ask,
          currencyPairPrecision + 1,
          currencyPairPrecision,
        ),
      };

  // use to highlight the main rate for a RFS
  const ratesWithoutMargin = {
    bid: convertPointsToRate(
      convertRateToPoints(rfs.quote.spotWithoutMargin.bid, currencyPairPrecision) -
        10 * rfs.quote.forwardPointsWithoutMargin.bid,
      currencyPairPrecision + 1,
      currencyPairPrecision,
    ),
    ask: convertPointsToRate(
      convertRateToPoints(rfs.quote.spotWithoutMargin.ask, currencyPairPrecision) +
        10 * rfs.quote.forwardPointsWithoutMargin.ask,
      currencyPairPrecision + 1,
      currencyPairPrecision,
    ),
  };

  const spread = roundToFixed(convertRateToPoints(rates.ask - rates.bid, currencyPairPrecision) / 10, 1);

  const commercialMarginAmount = getCashRfsMarginAmount({
    currencyPairPrecision,
    notional,
    notionalCurrencyIndex,
    commercialMarginSpotPoints,
    commercialMarginSwapPoints,
    commercialMarginAmountCurrencyIndex,
    rfsPrice: rfs,
    rates,
    ratesWithoutMargin,
  });

  /**
   * RFS: streamState.status === 'PRICING' && streamState.quote.quoteType === 'Tradeable'
   */
  const isExecutable = rfs.status === 'STREAMED' && rfs.quote.quoteType === 'Tradeable';

  const isReadonly = !canTrade || !isStreamingConnected;

  return {
    status: 'rfs-price' as const,

    keepEsp,

    rfsId: rfs.rfsId,
    quoteId: rfs.quote.quoteId,

    currencyPairPrecision,

    isExecutable,
    isReadonly,
    isSaleTier: rfs.quote.distributionStream === 'STREAMDEFAULT',

    time: {
      window: rfs.quote.rfsWindow,
      remaining: rfs.quote.rfsRemainingTime,
    },

    frontTimestamp: rfs.quote.frontTimestamp,
    backTimestamp: rfs.quote.backTimestamp,

    trader: {
      id: rfs.quote.traderId,
    },

    negotiations: rfs.negotiations,

    commercialMarginSpotPoints,
    commercialMarginDefaultSpotPoints: rfs.commercialMarginDefaultSpotPoints,

    commercialMarginSwapPoints,
    commercialMarginDefaultSwapPoints: rfs.commercialMarginDefaultSwapPoints,

    commercialMarginAmount,
    commercialMarginAmountCurrencyIndex: rfs.commercialMarginAmountCurrencyIndex,
    commercialMarginAmountCurrency,

    rates,
    ratesWithoutMargin,
    spread,

    spotRates,
    swapPoints,

    // The execution spot rates differ by user type (see fx-web/app getCashRfsSpotPriceWithMargin):
    // - Internal Sales: spot with margin is recomputed from SpotWithoutMargin (backend) ± commercial margin.
    //   The backend sends SpotWithoutMargin + DefaultSpotMargin for internal users so that the front
    //   can apply the (potentially user-edited) margin.
    // - External / Internal Trader: spot with margin is taken directly from SpotWithMargin (backend),
    //   which already embeds the margin calculated by the pricing engine.
    execution: {
      spotWithMargin: isInternalSale
        ? {
            bid: convertPointsToRate(
              convertRateToPoints(rfs.quote.spotWithoutMargin.bid, currencyPairPrecision) -
                10 * commercialMarginSpotPoints.bid,
              currencyPairPrecision + 1,
              currencyPairPrecision,
            ),
            ask: convertPointsToRate(
              convertRateToPoints(rfs.quote.spotWithoutMargin.ask, currencyPairPrecision) +
                10 * commercialMarginSpotPoints.ask,
              currencyPairPrecision + 1,
              currencyPairPrecision,
            ),
          }
        : {
            bid: rfs.quote.spotWithMargin.bid,
            ask: rfs.quote.spotWithMargin.ask,
          },
      spotWithoutMargin: rfs.quote.spotWithoutMargin,
    },
  };
};

type GetCashRfsMarginAmountProps = {
  currencyPairPrecision: number;

  notional: number;
  notionalCurrencyIndex: CurrencyIndex;

  commercialMarginSpotPoints: BidAskPoint;
  commercialMarginSwapPoints: BidAskPoint;
  commercialMarginAmountCurrencyIndex: CurrencyIndex;

  rfsPrice: RfsPrice;
  rates: BidAskRate;
  ratesWithoutMargin: BidAskRate;
};

// see https://jira.world.socgen/browse/SGEFX-1637
export const getCashRfsMarginAmount = (props: GetCashRfsMarginAmountProps) => {
  const notionalCurrencyIndex = props.notionalCurrencyIndex ?? 1;
  const marginAmountCurrencyIndex = props.commercialMarginAmountCurrencyIndex ?? 2;

  // currency pair EUR/USD
  // sample:
  // - notional currency EUR
  // - margin amount currency USD
  // => "1/2"
  switch (`${marginAmountCurrencyIndex}/${notionalCurrencyIndex}`) {
    case '1/1': // in sample: EUR/EUR
      return getCashRfsMarginCcy1AmountFromNotionalInCcy1(props);

    case '1/2': // in sample: EUR/USD
      return getCashRfsMarginCcy1AmountFromNotionalInCcy2(props);

    case '2/1': // in sample: USD/EUR
      return getCashRfsMarginCcy2AmountFromNotionalInCcy1(props);

    case '2/2': // in sample: USD/USD
      return getCashRfsMarginCcy2AmountFromNotionalInCcy2(props);

    default:
      throw new Error(
        `unexpected currency selection notionalCurrencyIndex=${notionalCurrencyIndex} - marginAmountCurrencyIndex=${marginAmountCurrencyIndex}`,
      );
  }
};

const getCashRfsMarginCcy1AmountFromNotionalInCcy1 = (props: GetCashRfsMarginAmountProps) => {
  const rfsPrice = props.rfsPrice;
  if (rfsPrice.status !== 'STREAMED') {
    throw new Error('Expected streamed status');
  }

  const notional = props.notional ?? 0;
  const spotRate = rfsPrice.quote.midAllInRate;

  if (isNotDefined(spotRate) || notional === 0) {
    return {
      bid: 0,
      ask: 0,
    };
  }

  const spotBidMarginPoints = props.commercialMarginSpotPoints?.bid ?? 0;
  const spotAskMarginPoints = props.commercialMarginSpotPoints?.ask ?? 0;

  const forwardBidMarginPoints = props.commercialMarginSwapPoints?.bid ?? 0;
  const forwardAskMarginPoints = props.commercialMarginSwapPoints?.ask ?? 0;

  const pointScale = 10 ** props.currencyPairPrecision;

  const spotBidMarginRate = ((spotBidMarginPoints + forwardBidMarginPoints) * 10) / pointScale;
  const spotAskMarginRate = ((spotAskMarginPoints + forwardAskMarginPoints) * 10) / pointScale;

  const bidRate = rfsPrice.quote.spotWithoutMargin.bid;
  const askRate = rfsPrice.quote.spotWithoutMargin.ask;

  return {
    bid: Math.round((notional * spotBidMarginRate) / askRate),
    ask: Math.round((notional * spotAskMarginRate) / bidRate),
  };
};

const getCashRfsMarginCcy1AmountFromNotionalInCcy2 = (props: GetCashRfsMarginAmountProps) => {
  const rfsPrice = props.rfsPrice;
  if (rfsPrice.status !== 'STREAMED') {
    throw new Error('Expected streamed status');
  }
  const notional = props.notional ?? 0;

  const bidRate = rfsPrice.quote.spotWithoutMargin.bid;
  const askRate = rfsPrice.quote.spotWithoutMargin.ask;

  if (isNotDefined(bidRate) || isNotDefined(askRate) || notional === 0) {
    return {
      bid: 0,
      ask: 0,
    };
  }

  const spotBidMarginPoints = props.commercialMarginSpotPoints?.bid ?? 0;
  const spotAskMarginPoints = props.commercialMarginSpotPoints?.ask ?? 0;

  const forwardBidMarginPoints = props.commercialMarginSwapPoints?.bid ?? 0;
  const forwardAskMarginPoints = props.commercialMarginSwapPoints?.ask ?? 0;

  const pointScale = 10 ** props.currencyPairPrecision;

  const spotBidMarginRate = ((spotBidMarginPoints + forwardBidMarginPoints) * 10) / pointScale;
  const spotAskMarginRate = ((spotAskMarginPoints + forwardAskMarginPoints) * 10) / pointScale;

  return {
    bid: Math.round((notional * spotBidMarginRate) / bidRate / bidRate),
    ask: Math.round((notional * spotAskMarginRate) / askRate / askRate),
  };
};

const getCashRfsMarginCcy2AmountFromNotionalInCcy1 = (props: GetCashRfsMarginAmountProps) => {
  const rfsPrice = props.rfsPrice;
  if (rfsPrice.status !== 'STREAMED') {
    throw new Error('Expected streamed status');
  }
  const notional = props.notional ?? 0;

  const bidRate = rfsPrice.quote.spotWithoutMargin.bid;
  const askRate = rfsPrice.quote.spotWithoutMargin.ask;

  if (isNotDefined(bidRate) || isNotDefined(askRate) || notional === 0) {
    return {
      bid: 0,
      ask: 0,
    };
  }

  const spotBidMarginPoints = props.commercialMarginSpotPoints?.bid ?? 0;
  const spotAskMarginPoints = props.commercialMarginSpotPoints?.ask ?? 0;

  const forwardBidMarginPoints = props.commercialMarginSwapPoints?.bid ?? 0;
  const forwardAskMarginPoints = props.commercialMarginSwapPoints?.ask ?? 0;

  const pointScale = 10 ** props.currencyPairPrecision;

  const spotBidMarginRate = ((spotBidMarginPoints + forwardBidMarginPoints) * 10) / pointScale;
  const spotAskMarginRate = ((spotAskMarginPoints + forwardAskMarginPoints) * 10) / pointScale;

  return {
    bid: Math.round(notional * spotBidMarginRate),
    ask: Math.round(notional * spotAskMarginRate),
  };
};

const getCashRfsMarginCcy2AmountFromNotionalInCcy2 = (props: GetCashRfsMarginAmountProps) => {
  const rfsPrice = props.rfsPrice;
  if (rfsPrice.status !== 'STREAMED') {
    throw new Error('Expected streamed status');
  }
  const notional = props.notional ?? 0;

  const bidRate = rfsPrice.quote.spotWithoutMargin.bid;
  const askRate = rfsPrice.quote.spotWithoutMargin.ask;

  if (isNotDefined(bidRate) || isNotDefined(askRate) || notional === 0) {
    return {
      bid: 0,
      ask: 0,
    };
  }

  const bidNotionalInCcy1 = notional / props.rates.bid;
  const askNotionalInCcy1 = notional / props.rates.ask;

  const spotBidMarginPoints = props.commercialMarginSpotPoints?.bid ?? 0;
  const spotAskMarginPoints = props.commercialMarginSpotPoints?.ask ?? 0;

  const forwardBidMarginPoints = props.commercialMarginSwapPoints?.bid ?? 0;
  const forwardAskMarginPoints = props.commercialMarginSwapPoints?.ask ?? 0;

  const pointScale = 10 ** props.currencyPairPrecision;

  const spotBidMarginRate = ((spotBidMarginPoints + forwardBidMarginPoints) * 10) / pointScale;
  const spotAskMarginRate = ((spotAskMarginPoints + forwardAskMarginPoints) * 10) / pointScale;

  return {
    bid: Math.round(bidNotionalInCcy1 * spotBidMarginRate),
    ask: Math.round(askNotionalInCcy1 * spotAskMarginRate),
  };
};
